'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Bot, Menu, Search } from 'lucide-react';
import { cn } from '@utils/cn';
import { getMoreNavItems, isRouteItemActive } from '@utils/route-utils';
import { Pressable } from '@components/mobile/controls/pressable';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@components/ui/dropdown-menu';
import AppAddCreateButton from '@components/AppAddCreateButton';
import { useAgentsMembershipEnabled } from '@hooks/use-agents-membership';
import { useSearchCommand } from '@hooks/use-search-command';
import { useStartAgentChat } from '@hooks/use-start-agent-chat';

interface HeaderActionsProps {
  organization_id: string;
}

const CHROME_CIRCLE =
  'bg-card inline-flex size-11 shrink-0 items-center justify-center rounded-full text-foreground';

/**
 * Top-right Plus + Search + Menu circles (and an AI chat circle when agents
 * are enabled). Plus is route-contextual; Search opens the global search
 * drawer; Menu hosts Team and Utilities destinations that used to live in
 * the 5th tab.
 */
export function HeaderActions({ organization_id }: HeaderActionsProps) {
  const t = useTranslations();
  const pathname = usePathname();
  const { setOpen: setSearchOpen } = useSearchCommand();
  const agentsEnabled = useAgentsMembershipEnabled();
  const { start: startAgentChat, loading: startingAgentChat } =
    useStartAgentChat(organization_id);
  const moreItems = getMoreNavItems(organization_id, t);

  return (
    <div className="flex items-center gap-1.5">
      <AppAddCreateButton organization_id={organization_id} variant="chrome" />

      {agentsEnabled ? (
        <Pressable
          feedback="scale"
          aria-label={t('common.ai-chat')}
          onClick={() => void startAgentChat()}
          disabled={startingAgentChat}
          className={CHROME_CIRCLE}
        >
          <Bot className="size-5" />
        </Pressable>
      ) : null}
      <Pressable
        feedback="scale"
        aria-label={t('common.search')}
        onClick={() => setSearchOpen(true)}
        className={CHROME_CIRCLE}
      >
        <Search className="size-5" />
      </Pressable>

      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={t('navigation.menu')}
          className={cn(
            CHROME_CIRCLE,
            'transition-transform duration-fast ease-out active:scale-[0.97]'
          )}
        >
          <Menu className="size-5" aria-hidden />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={8}
          collisionPadding={12}
          aria-label={t('navigation.menu')}
          className="w-[min(19rem,calc(100vw-2rem))] rounded-[var(--mobile-chrome-squircle)] border-0 bg-popover p-2 text-popover-foreground shadow-xl data-[state=closed]:duration-fast data-[state=open]:duration-normal data-[state=closed]:ease-out data-[state=open]:ease-out"
        >
          <DropdownMenuLabel className="px-3 pb-2 pt-2 text-meta font-normal tracking-wider text-muted-foreground uppercase">
            {t('navigation.menu')}
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="mx-1 mb-1 mt-0" />
          {moreItems.map(item => {
            const active = isRouteItemActive(pathname, item);
            return (
              <DropdownMenuItem
                key={item.id}
                asChild
                className={cn(
                  'min-h-12 cursor-pointer gap-3 rounded-xl px-3 text-body text-muted-foreground transition-[color,background-color,transform] duration-fast ease-out active:scale-[0.98] focus:bg-accent focus:text-foreground',
                  active && 'bg-accent text-foreground'
                )}
              >
                <Link
                  href={item.href}
                  aria-current={active ? 'page' : undefined}
                >
                  {item.icon ? (
                    <item.icon className="size-5 opacity-80" aria-hidden />
                  ) : null}
                  <span className="flex-1 font-medium">{item.label}</span>
                </Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
